import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import AddMemberToQuote from '@components/assign-team-member/AddMemberToQuote';
import SampleComment from '@components/SampleComment';
import { toastSuccess } from '@components/Toast';
import TopDetails from '@components/TopDetails';

import { clearCurrentActivePage, setCurrentActivePage } from '@store/actions/ActivePageAction';
import { fetchCostingDetails } from '@store/actions/CostingAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { onUpdateEOD } from '@services/quoteServices';

import {
	changeDateFormat,
	getTodaysDate,
	getUserInfo,
	packTag,
	renderSetImages,
	setTag,
	STATIC_SET_CONSTANTS
} from '@utils/Util';

import AddTaskModal from '@/components/AddTaskModal';
import CostingAdd from '@/features/collection/components/CostingAdd';
import { clearDesignViewModalInfo, setDesignViewModalInfo } from '@/store/actions/DesignViewModalAction';
import ApiClient from '@/lib/apiClient';

const QuoteRequestDetails = () => {
	const [costingDetails, setCostingDetails] = useState(null);
	const [showComment, setShowComment] = useState(false);
	const [selectedId, setSelectedId] = useState(null);
	const [idsForChangeDate, setIdsForChangeDate] = useState([]);
	const costingStore = useSelector((store) => store.costingStore);
	const costingItem = costingDetails?.quoteItemResponseList;
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { id } = useParams();
	const [selectedProductIds, setSelectedProductIds] = useState([]);
	const [isShowTaskModal, setIsShowTaskModal] = useState(false);
	const [isShowCostingModal, setIsShowCostingModal] = useState(false);
	const [buyerPrice, setBuyerPrice] = useState(null);

	useEffect(() => {
		if (costingDetails?.quoteApprovalStatus === 'APPROVED') {
			navigate(`/costing/${costingDetails?.id}`, { replace: true });
		}
	}, [costingDetails?.quoteApprovalStatus]);

	useEffect(() => {
		setCostingDetails(costingStore.costingDetails);
	}, [costingStore]);

	const fetchBuyerRequestPriceRange = async () => {
		try {
			const { data } = await ApiClient.GET('fetchBuyerRequestPriceRange', `${id}`);
			setBuyerPrice(data?.payload);
		} catch {
			// error;
		}
	};

	useEffect(() => {
		if (id) {
			fetchBuyerRequestPriceRange();
		}
	}, [id]);

	const formatPriceRange = (buyerPrice, productId) => {
		const priceData = buyerPrice?.[productId];
		if (!priceData?.medianPrice || !priceData?.avgPrice) return '';

		const median = priceData?.medianPrice ? parseFloat(priceData.medianPrice) : 0;
		const avg = priceData?.avgPrice ? parseFloat(priceData.avgPrice) : 0;
		return `$${Math.min(median, avg).toFixed(2)} - $${Math.max(median, avg).toFixed(2)}`;
	};

	const fetchCostingList = () => {
		dispatch(openLoader());
		dispatch(fetchCostingDetails(id)).finally(() => {
			dispatch(closeLoader());
		});
	};

	useEffect(() => {
		fetchCostingList();
		return () => {
			dispatch(clearCurrentActivePage());
		};
	}, []);

	const onClearChat = () => {
		setCostingDetails((prevQuoteStore) => ({
			...prevQuoteStore,
			quoteItemResponseList: prevQuoteStore?.quoteItemResponseList?.map((item) => {
				if (item.productId === selectedId) {
					return {
						...item,
						noOfUnreadMessage: 0
					};
				}
				return item;
			})
		}));
		setSelectedId(null);
	};

	const handleCommentClose = () => {
		onClearChat();
		setShowComment(false);
		dispatch(clearCurrentActivePage());
	};

	// const onApprove = async () => {
	// 	try {
	// 		dispatch(openLoader());
	// 		const { data } = await ApiClient.POST('quoteRequest', {}, `${id}/approve`);
	// 		dispatch(closeLoader());
	// 		if (data) {
	// 			navigate(`/costing/${id}`, { replace: true });
	// 		}
	// 	} catch ({ response }) {
	// 		dispatch(closeLoader());
	// 		if (response && response.data && response.data.message) {
	// 			toastError(response.data.message);
	// 		} else {
	// 			toastError('Something went wrong! Please try again.');
	// 		}
	// 	}
	// };

	// const onCancel = async () => {
	// 	try {
	// 		dispatch(openLoader());
	// 		const { data } = await ApiClient.POST('quoteRequest', {}, `${id}/cancel`);
	// 		dispatch(closeLoader());
	// 		if (data) {
	// 			navigate('/buyer-requests');
	// 		}
	// 	} catch ({ response }) {
	// 		dispatch(closeLoader());
	// 		if (response && response.data && response.data.message) {
	// 			toastError(response.data.message);
	// 		} else {
	// 			toastError('Something went wrong! Please try again.');
	// 		}
	// 	}
	// };

	const handleCheckAllItem = (e) => {
		if (e.target.checked) {
			const allProductIds = costingDetails?.quoteItemResponseList
				?.filter(
					(product) =>
						product?.quoteItemStatus !== 'APPROVED' && product?.quoteItemStatus !== 'CANCELLED'
				)
				?.map((item) => item.productId);
			setSelectedProductIds(allProductIds);
		} else {
			setSelectedProductIds([]);
		}
	};

	const getStatus = (status) => {
		if (status === 'APPROVED') return 'green';
		// if (status === 'REQUEST_FOR_REVISION') return 'green';
		return 'secondary';
	};

	const isProductHasSet = (product) => {
		return product.isSet || product.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT;
	};

	const isProductHasPack = (product) => {
		return product.isPack || product.setPackStyleFlag === STATIC_SET_CONSTANTS.PACK_PRODUCT;
	};

	const getOfferPrice = (item) => (
		<p className={`regular-14 ${getStatus(item.status)} weight-500`}>
			{item?.adminOfferPrice && (
				<span className="d-block">
					${parseFloat(item?.adminOfferPrice).toFixed(2)}
					{item?.margin && <span className="pl-1 gray_dark_01">({item?.margin}%)</span>}
				</span>
			)}
			{item?.priceUpdateDTOArray &&
				item?.priceUpdateDTOArray?.map((price, idx) => (
					<p className="regular-12 gray_dark_02 weight-500" key={`price_${idx}`}>
						<del>${parseFloat(price?.previousValue).toFixed(2)}</del>
					</p>
				))}
		</p>
	);

	const handleDateChange = (value, productid) => {
		setCostingDetails((prevCostingDetails) => {
			const updatedQuoteItemResponseList = prevCostingDetails?.quoteItemResponseList?.map((item) => {
				if (item.expectedOrderDate && item.productId === productid) {
					item.expectedOrderDate = value;
					setIdsForChangeDate((prevIds) => [...new Set([...prevIds, item.productId])]);
				}

				if (!item.expectedOrderDate) {
					item.expectedOrderDate = value;
					setIdsForChangeDate((prevIds) => [...new Set([...prevIds, item.productId])]);
				}

				return item;
			});

			return {
				...prevCostingDetails,
				quoteItemResponseList: updatedQuoteItemResponseList
			};
		});
	};

	const onChangeDate = async (value) => {
		try {
			dispatch(openLoader());
			const response = await onUpdateEOD(id, idsForChangeDate, value); // Make the API call
			toastSuccess(response?.message);
		} finally {
			dispatch(closeLoader());
			setIdsForChangeDate([]);
		}
	};

	const handleBottomPopupAction = (actionType) => {
		if (actionType === 'CLEAR') {
			setSelectedProductIds([]);
		}
		if (actionType === 'SEND_FOR_COSTING') {
			setIsShowCostingModal(true);
		}
		if (actionType === 'UPDATE_TASK') {
			setIsShowTaskModal(true);
		}
	};
	const onCloseViewModal = () => {
		dispatch(clearDesignViewModalInfo({}));
	};

	const openDesignViewModal = (item) => {
		dispatch(
			setDesignViewModalInfo({
				isShowMergeTechPack:
					item?.isSet || item?.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT,
				id: item?.productId,
				onCloseViewModal: onCloseViewModal,
				source: 'PRODUCT_LIST'
				// designIdList: designIdList
			})
		);
	};

	return (
		<div>
			<div className="top-header bg-white">
				<div className="filter-container-top pt-3 pb-3 bg-gray">
					<div className="filters">
						<div className="sample-full-status">
							<ul className="common-list-component mb-2">
								<li>
									<img
										className="cursor-pointer"
										src={`${process.env.PUBLIC_URL}/icons/back-arrow-gray.svg`}
										alt="back"
										onClick={() => navigate(-1)}
									/>
									<span className="gray_dark_01 ml-2">
										{costingDetails?.title || costingDetails?.collectionName}
									</span>
								</li>
								<li>
									<span>{costingDetails?.referenceNumber} </span>
								</li>
								<li>
									<span>{costingDetails?.requestedBy}</span>
								</li>
								<li>
									<span>
										{changeDateFormat(costingDetails?.requestedDate, '', 'Do MMM')}
									</span>
								</li>
								<li>
									<span>{costingDetails?.noOfDesign} Design</span>
								</li>
							</ul>
						</div>
					</div>
					<div className="d-flex align-items-center">
						<AddMemberToQuote quoteId={costingDetails?.id} requestFrom="PENDING_REQUEST" />

						{/* {costingDetails?.quoteApprovalStatus === 'PENDING' && (
							<div className="ml-3">
								<button
									className="button gray-outline size32 mr-3"
									onClick={() => onCancel()}
								>
									<span>Cancel</span>
								</button>
								<button
									disabled={isApproveBtnDisabled}
									className="button approve size32"
									onClick={() => onApprove()}
								>
									<span>Approve</span>
								</button>
							</div>
						)} */}
					</div>
				</div>
			</div>
			<div className="sample-request-gorup mt-0 px-4">
				<div className="costing-dashboard-container bg-gray">
					<TopDetails
						type={'QUOTE'}
						description={costingDetails?.description}
						documentLists={costingDetails?.requestedDocumentList}
					/>
					<div className="scroll-x-label">
						<table className="table with-border">
							<thead>
								<tr>
									<th className="select-box-column">
										<div className="custom-chekbox">
											<div className="form-group m-0">
												<input
													id="check_all"
													name="costing check all"
													type="checkbox"
													checked={
														selectedProductIds?.length ===
														costingDetails?.quoteItemResponseList?.filter(
															(design) =>
																design?.quoteItemStatus !== 'APPROVED' &&
																design?.quoteItemStatus !== 'CANCELLED'
														)?.length
													}
													onChange={handleCheckAllItem}
												/>
												<label htmlFor="check_all" />
											</div>
										</div>
									</th>

									<th>Design({costingDetails?.quoteItemResponseList?.length})</th>
									<th>Description</th>
									<th>Qty</th>
									<th>Usual buying price</th>
									<th>Target</th>
									<th>Production price</th>
									{getUserInfo().primaryUserType !== 'COSTING_MANAGER' && (
										<th>Offer price</th>
									)}
									<th>Expected Order Date</th>
									<th>Remarks</th>
									<th>Conversation</th>
								</tr>
							</thead>
							<tbody>
								{costingItem?.map((item, idx) => (
									<tr
										key={`${item.quantityWiseCostingId}_${idx}`}
										className={
											['APPROVED', 'CANCELLED'].includes(item?.quoteItemStatus)
												? 'disabled-row'
												: ''
										}
										style={
											['APPROVED', 'CANCELLED'].includes(item?.quoteItemStatus)
												? { pointerEvents: 'none', opacity: 0.6 }
												: {}
										}
									>
										<td className="select-box-column">
											<div className="custom-chekbox">
												<div className="form-group m-0">
													<input
														id={`checkbox__${item?.productId}`}
														name={item?.productId}
														type="checkbox"
														checked={selectedProductIds?.includes(
															item?.productId
														)}
														onChange={(e) => {
															if (e.target.checked) {
																setSelectedProductIds((prevIds) => [
																	...prevIds,
																	item?.productId
																]);
															} else {
																setSelectedProductIds((prevIds) =>
																	prevIds.filter(
																		(id) => id !== item?.productId
																	)
																);
															}
														}}
														disabled={['APPROVED', 'CANCELLED'].includes(
															item?.quoteItemStatus
														)}
													/>

													<label htmlFor={`checkbox__${item?.productId}`} />
												</div>
											</div>
										</td>
										<td
											className={`${item.isSameProduct && 'd-none'}`}
											rowSpan={item?.noOfSameProduct}
										>
											<div
												className="d-flex cursor-pointer"
												onClick={() => openDesignViewModal(item)}
											>
												<div className="design-image">
													{isProductHasSet(item) ? (
														renderSetImages(item?.setMemberList, '56px')
													) : (
														<img
															src={item?.featureImageDocUrl}
															alt="image"
															className="cell-img pr-2"
														/>
													)}
												</div>
												<div className="ml-1">
													<div className="mb-1">
														{isProductHasSet(item) && setTag()}
														{isProductHasPack(item) && packTag()}
													</div>
													{item?.productRefNo} <br />
													<ul className="common-list-component mb-2">
														<li>
															{isProductHasSet(item)
																? item?.setMemberList[0]?.market
																: item?.market}
														</li>
														{isProductHasSet(item) && item?.setMemberList ? (
															item?.setMemberList.map((member) => (
																<li key={member?.id}>{member?.category} </li>
															))
														) : (
															<li>{item?.category} </li>
														)}
													</ul>
													{item?.quoteItemStatus === 'APPROVED' && (
														<div className="task-status green">
															<span className="status-btn ">
																{item?.quoteItemStatus}
															</span>
														</div>
													)}
													{item?.quoteItemStatus === 'CANCELLED' && (
														<div className="task-status red">
															<span className="status-btn ">
																{item?.quoteItemStatus}
															</span>
														</div>
													)}
												</div>
											</div>
										</td>
										<td
											className={`cursor-pointer ${item.isSameVariant && 'd-none'}`}
											rowSpan={item?.noOfSameVariant}
										>
											{isProductHasSet(item) && item?.setMemberList ? (
												<ul className="with-bullet">
													{item?.setMemberList.map((member) => (
														<li key={member?.id}>{member?.variation}</li>
													))}
												</ul>
											) : (
												<span className="cursor-pointer">
													{item?.variation || ''}
												</span>
											)}
										</td>
										<td
											className="cursor-pointer"
											data-toggle="modal"
											data-target="#designViewModal"
										>
											<span className="regular-14 gray_dark_01">{item?.quantity}</span>
										</td>
										<td className="buyer-price-range">
											<span className="regular-14 gray_dark_01">
												{formatPriceRange(buyerPrice, item.productId)}
											</span>
										</td>
										<td>
											<span className="regular-14 gray_dark_01">{item?.target}</span>
										</td>
										<td
											className="cursor-pointer"
											data-toggle="modal"
											data-target="#designViewModal"
										>
											<span className="regular-14 gray_dark_01">{item?.price}</span>
										</td>
										{getUserInfo().primaryUserType !== 'COSTING_MANAGER' && (
											<td>{getOfferPrice(item)}</td>
										)}
										<td
											className={`${item.isSameProduct && 'd-none'}`}
											rowSpan={item?.noOfSameProduct}
										>
											<input
												type="date"
												name="eoDate"
												min={getTodaysDate()}
												value={item?.expectedOrderDate}
												onChange={(e) =>
													handleDateChange(e.target.value, item?.productId)
												}
												onBlur={(e) => onChangeDate(e.target.value, item?.productId)}
											/>
										</td>
										<td>
											<span className="regular-14 gray_dark_02">{item?.remarks}</span>
										</td>
										<td>
											<div className="conversation-btn">
												<button
													className="button text"
													onClick={() => {
														dispatch(
															setCurrentActivePage({
																id: item?.productId,
																type: 'PRODUCT_CONVERSATION',
																pageType: 'DETAILS'
															})
														);
														setSelectedId(item?.productId);
														setShowComment(true);
													}}
												>
													{item?.noOfUnreadMessage > 0 ? (
														<img
															src={`${process.env.PUBLIC_URL}/icons/comment-not-read.svg`}
															alt="not read"
														/>
													) : (
														<img
															src={`${process.env.PUBLIC_URL}/icons/comments-gray.svg`}
															alt="not read"
														/>
													)}
												</button>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			{selectedProductIds?.length > 0 && (
				<div
					className={
						'design-count-section bg-white design-list-count favourite-list-count shadow-8dp'
					}
				>
					<div className="count-info d-flex justify-content-between align-items-center">
						<div className="count-info d-flex justify-content-between align-items-center">
							<div className="design-count">
								<p className="semibold-18">{selectedProductIds?.length}</p>
							</div>

							<span className="regular-16 gray_dark_01 d-inline-block mx-2  selection-devider">
								|
							</span>
							<button
								className="button text size36"
								onClick={() => handleBottomPopupAction('SEND_FOR_COSTING')}
							>
								<span className="">Send for costing</span>
							</button>
							<button
								className="button text size36"
								onClick={() => handleBottomPopupAction('UPDATE_TASK')}
							>
								<span className="">Update task</span>
							</button>
						</div>
						<div
							className="text-right close-icon"
							onClick={() => handleBottomPopupAction('CLEAR')}
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="25"
								height="24"
								viewBox="0 0 25 24"
								fill="none"
							>
								<path
									d="M18.5038 5.7107C18.1138 5.3207 17.4838 5.3207 17.0938 5.7107L12.2038 10.5907L7.31383 5.7007C6.92383 5.3107 6.29383 5.3107 5.90383 5.7007C5.51383 6.0907 5.51383 6.7207 5.90383 7.1107L10.7938 12.0007L5.90383 16.8907C5.51383 17.2807 5.51383 17.9107 5.90383 18.3007C6.29383 18.6907 6.92383 18.6907 7.31383 18.3007L12.2038 13.4107L17.0938 18.3007C17.4838 18.6907 18.1138 18.6907 18.5038 18.3007C18.8938 17.9107 18.8938 17.2807 18.5038 16.8907L13.6138 12.0007L18.5038 7.1107C18.8838 6.7307 18.8838 6.0907 18.5038 5.7107Z"
									fill="#6A7181"
								/>
							</svg>
						</div>
					</div>
				</div>
			)}

			{isShowCostingModal && (
				<CostingAdd
					selectedDesignIds={selectedProductIds}
					onClose={() => setIsShowCostingModal(false)}
					isEdit={true}
					costingDetails={costingDetails}
					requestFrom="PENDING_REQUEST"
				/>
			)}

			{isShowTaskModal && (
				<AddTaskModal
					onClose={() => setIsShowTaskModal(false)}
					productIdList={selectedProductIds}
					onSuccess={() => {
						setSelectedProductIds([]);
						fetchCostingList();
					}}
					params={{ taskValueType: 'CANCEL_BUYER_PLATFORM_COSTING_REQUEST' }}
				/>
			)}

			<div className="col-md-1 one-sixth text-right">
				{selectedId && (
					<SampleComment
						sampleRequestId={costingDetails?.id || id}
						postTypeFilter={'PRODUCT_DEVELOPMENT_COMMENT'}
						topic={'PRODUCT_QUOTE_COMMENT'}
						postTypeId={'quoteRequestId'}
						productId={selectedId}
						showComment={showComment}
						setShowComment={handleCommentClose}
						brandInfo={{
							brandId: costingDetails.brandId,
							brandName: costingDetails.brandName
						}}
					/>
				)}
			</div>
		</div>
	);
};

export default QuoteRequestDetails;
